import { useEffect, useState, useCallback } from "react";
import {
  Button,
  DialogContentText,
  FormControl,
  MenuItem,
  TextField,
  alpha,
  useTheme,
  Divider,
  Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { CONFIG } from "../../config";
import { StorageOutlined } from "@mui/icons-material";
import DialogBase from "./DialogBase";
import { UtilityService } from "../../services/UtilityService";
import { OrganisationService } from "../../services/OrganisationService";
import { DeviceService } from "../../services/DeviceService";
import { useSelfHostDevice } from "../../hooks/queries/useDevices";
import { logger } from "../../utils/logger";

export default function SelfHostDialog(props) {
  const selectedOrganisationID = localStorage.getItem("selectedOrganisationID");
  const { open, handleClose } = props;
  const { t } = useTranslation("common");
  const theme = useTheme();

  const initialOrganisationId = selectedOrganisationID
    ? String(selectedOrganisationID)
    : "";

  const [organisationID, setOrganisationID] = useState(initialOrganisationId);
  const [organisations, setOrganisations] = useState([]);
  const [dev_eui, setDev_eui] = useState("");
  const [name, setName] = useState("");
  const [deviceBrands, setDeviceBrands] = useState([]);
  const [deviceBrand, setDeviceBrand] = useState("");

  // TanStack Query mutations
  const selfHostDeviceMutation = useSelfHostDevice();

  // Custom text field styling for consistent UI appearance
  const textFieldStyle = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: alpha(theme.palette.background.paper, 0.8),
      "&:hover": {
        boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.1)",
      },
      "&.Mui-focused": {
        boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: alpha(theme.palette.divider, 0.3),
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.875rem",
      fontWeight: 500,
    },
    mt: 2,
  };

  async function onboardSelfHosted() {
    if (!dev_eui) {
      UtilityService.updateSnackbarMessage(t("common.please_enter_device_eui"));
      return;
    }

    if (!name) {
      UtilityService.updateSnackbarMessage(
        t("common.please_enter_device_name")
      );
      return;
    }

    if (!deviceBrand) {
      UtilityService.updateSnackbarMessage(
        t("common.please_select_device_type")
      );
      return;
    }

    // Check dev_eui is valid
    if (!dev_eui.match(/^[0-9a-fA-F]{16}$/)) {
      UtilityService.updateSnackbarMessage(
        t("common.please_enter_valid_device_eui")
      );
      return;
    }

    try {
      await selfHostDeviceMutation.mutateAsync({
        organisation_id: organisationID ? Number(organisationID) : undefined,
        dev_eui: dev_eui,
        name: name,
        device_brand: deviceBrand,
      });

      UtilityService.updateSnackbarMessage(t("common.self_host_success"));
      handleDialogClose();
    } catch (error) {
      logger.warn(error);
      UtilityService.updateSnackbarMessage(
        t("common.self_host_error") +
          " " +
          (error.response?.data?.detail || error.message)
      );
    }
  }

  // Clear all fields when dialog is closed
  const clearFields = () => {
    setName("");
    setDev_eui("");
    setDeviceBrand("");
  };

  useEffect(() => {
    async function getOrganisations() {
      const response = await OrganisationService.fetchOrganisations();
      setOrganisations(response);

      // Set organisation id to first organisation if not already set
      if (response.length > 0 && !organisationID) {
        setOrganisationID(String(response[0].id));
      }
    }

    async function getDeviceBrands() {
      const response = await DeviceService.fetchDeviceBrands();
      setDeviceBrands(response);
    }
    getOrganisations();
    getDeviceBrands();
  }, [organisationID]);

  // Wrap handleClose to also clear fields
  const handleDialogClose = useCallback(() => {
    clearFields();
    handleClose();
  }, [handleClose]);

  return (
    <DialogBase
      open={open}
      handleClose={handleDialogClose}
      title={t("common.self_host_title")}
      icon={<StorageOutlined fontSize="medium" />}
      actions={
        <>
          <Button
            variant="outlined"
            onClick={handleDialogClose}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 500,
              px: 3,
              color:
                theme.palette.mode === "dark"
                  ? "#00c9a7"
                  : theme.palette.secondary.main,
              borderColor: alpha(
                theme.palette.mode === "dark"
                  ? "#00c9a7"
                  : theme.palette.secondary.main,
                0.5
              ),
              "&:hover": {
                borderColor:
                  theme.palette.mode === "dark"
                    ? "#00c9a7"
                    : theme.palette.secondary.main,
                backgroundColor: alpha(
                  theme.palette.mode === "dark"
                    ? "#00c9a7"
                    : theme.palette.secondary.main,
                  0.05
                ),
              },
            }}
          >
            {t("common.cancel")}
          </Button>

          <Button
            variant="contained"
            onClick={onboardSelfHosted}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              py: 1,
              px: 3,
              fontWeight: 500,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "#00c9a7"
                  : theme.palette.primary.main,
              color: theme.palette.mode === "dark" ? "#000" : "#fff",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? alpha("#00c9a7", 0.9)
                    : alpha(theme.palette.primary.main, 0.9),
              },
            }}
          >
            {t("common.add_device")}
          </Button>
        </>
      }
    >
      <DialogContentText sx={{ mb: 2, fontSize: "0.95rem" }}>
        {t("common.self_host_intro", { endpoint: CONFIG.API_URL })}
      </DialogContentText>

      <Stack spacing={2}>
        <FormControl fullWidth>
          <TextField
            id="name"
            label={t("common.name")}
            variant="outlined"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
            sx={textFieldStyle}
            placeholder={t("common.enter_device_name")}
          />
        </FormControl>

        <FormControl fullWidth>
          <TextField
            id="dev_eui"
            label={t("common.dev_eui")}
            variant="outlined"
            value={dev_eui}
            onChange={(event) => {
              setDev_eui(event.target.value);
            }}
            sx={textFieldStyle}
            placeholder={t("common.hex_16_chars")}
            helperText="Example: 70B3D57ED0051234"
          />
        </FormControl>

        <FormControl fullWidth>
          <TextField
            id="deviceBrand"
            label={t("common.device_type")}
            variant="outlined"
            select
            value={deviceBrand}
            onChange={(event) => {
              setDeviceBrand(event.target.value);
            }}
            sx={textFieldStyle}
          >
            {deviceBrands && deviceBrands.length > 0 ? (
              deviceBrands.map((brand) => (
                <MenuItem key={brand.id} value={brand.id}>
                  {brand.description}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                {t("common.no_device_types_available")}
              </MenuItem>
            )}
          </TextField>
        </FormControl>

        <FormControl fullWidth>
          <TextField
            id="organisation_id"
            label={t("common.organisation")}
            variant="outlined"
            select
            value={organisationID || ""}
            onChange={(event) => {
              setOrganisationID(String(event.target.value));
            }}
            sx={textFieldStyle}
          >
            {organisations && organisations.length > 0 ? (
              organisations.map((organisation) => (
                <MenuItem key={organisation.id} value={String(organisation.id)}>
                  {organisation.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                {t("common.no_organizations_available")}
              </MenuItem>
            )}
          </TextField>
        </FormControl>
      </Stack>

      <Divider />
    </DialogBase>
  );
}
